import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Tab, TabList, TabPanel, TabPanels, Tabs } from '@carbon/react';
import { api } from '../api/client';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';
import { PokerTablesTab } from './poker/PokerTablesTab';
import { PokerBanTaoTab } from './poker/PokerBanTaoTab';
import { PokerQuyBotTab } from './poker/PokerQuyBotTab';
import { PokerChoSanTab } from './poker/PokerChoSanTab';
import {
  chonBanPokerId,
  chonNguoiTrongBan,
  gheNgoi,
  gheNguoiThat,
  sapXepBanPoker,
  type Ban,
  type Lenh,
} from './pokerForceSort';
import { useAuth } from '../auth/AuthContext';
import './forced/forced.scss';

// Man dat ket qua poker. Xem bai moi ghe, dat thang / thua hoac chi dinh la
// cho van toi. Lenh khong sua van da xong.

type Man = { ban: Ban[]; lenh: Lenh[]; ban_loi: string | null };
const VONG = ['Preflop', 'Flop', 'Turn', 'River'];

function La({ ma }: { ma: string }) {
  return <span className={'fr-la' + (/[hd]$/.test(ma) ? ' fr-la--do' : '')}>{ma}</span>;
}
function chuanO(s: string) {
  const t = s.replace(/\s/g, '');
  if (!t) return '';
  return t.slice(0, 1).toUpperCase() + t.slice(1, 2).toLowerCase();
}
function tien(n: number) { return n.toLocaleString('vi-VN'); }

export function PokerForcePage() {
  const qc = useQueryClient();
  // Quy bot: chi chu so huu (API cung tu choi role khac)
  const chuSoHuu = useAuth().me?.role === 'founder';
  const q = useQuery({
    queryKey: ['poker-ep'],
    queryFn: () => api.get<Man>('/api/admin/games/poker'),
    refetchInterval: 2000,
  });
  const [chon, setChon] = useState<string | null>(null);
  const [xong, setXong] = useState<Command | null>(null);
  const gui = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<{ command: Command }>('/api/admin/games/poker', body),
    onSuccess: (r) => { setXong(r.command); qc.invalidateQueries({ queryKey: ['poker-ep'] }); },
  });

  const d = q.data;
  const banSapXep = d ? sapXepBanPoker(d.ban, d.lenh) : [];
  const chonId = d ? chonBanPokerId(banSapXep, d.lenh, chon) : null;
  useEffect(() => {
    if (chonId && chonId !== chon) setChon(chonId);
  }, [chonId, chon]);

  if (q.isLoading) return <BlockSkeleton lines={8} />;
  if (q.error || !d) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const ban = banSapXep.find((b) => b.id === chonId) ?? null;

  return (
    <div className="page fr">
      <PageHeader title="Quản lý & Đặt kết quả Poker"
        lead="Đặt kết quả ván bài tiếp theo theo bàn hoặc người chơi đang ngồi, quản lý cấu hình bật/tắt mức cược và từng bàn chơi poker, hoặc tạo bàn Texas / Omaha mới." />
      <Tabs>
        <TabList aria-label="Poker Tabs">
          <Tab>Đặt kết quả Poker</Tab>
          <Tab>Quản lý Bàn & Mức cược</Tab>
          <Tab>Tạo bàn</Tab>
          {chuSoHuu ? <Tab>Quỹ bot</Tab> : null}
          {chuSoHuu ? <Tab>Bot chờ</Tab> : null}
        </TabList>
        <TabPanels>
          <TabPanel>
            {xong ? <KetQua cmd={xong} onClose={() => setXong(null)} /> : null}
            {gui.error ? <InlineError error={gui.error} /> : null}
            {d.ban_loi && d.ban.length === 0
              ? <InlineNotification kind="warning" lowContrast hideCloseButton title="Chưa thấy bàn" subtitle="Máy game chưa trả lời." />
              : null}
            {!d.ban_loi && d.ban.length === 0
              ? <p className="muted">Chưa có bàn đang mở.</p>
              : null}
            {ban ? <BanForm key={ban.id} ban={ban} lenh={d.lenh.filter((l) => l.ban_id === ban.id)} gui={gui.mutate} dangGui={gui.isPending} /> : null}
            <div className="fr-list" style={{ marginTop: '1rem' }}>
              {banSapXep.map((b) => {
                const ngoi = gheNgoi(b);
                const nguoiThat = gheNguoiThat(b);
                const soLenh = d.lenh.filter((l) => l.ban_id === b.id).length;
                return (
                  <button key={b.id} type="button" className={'fr-card' + (b.id === ban?.id ? ' is-on' : '')} onClick={() => setChon(b.id)}>
                    <span className="fr-card__ten">{b.ten} · Mù {tien(b.muNho)}/{tien(b.muLon)}</span>
                    <span className="fr-card__id">{b.id} · {ngoi.length}/{b.soGhe} ghế{soLenh ? ` · ${soLenh} lệnh chờ` : ''}</span>
                    <span className="fr-readout">
                      {nguoiThat.length
                        ? `Người thật: ${nguoiThat.map((g) => `${g.ten} (#${g.userId})`).join(', ')}`
                        : ngoi.length
                          ? `Bot ngồi: ${ngoi.length}`
                          : 'Bàn trống'}
                    </span>
                    <span className="fr-card__foot">{b.van ? 'Đang ván' : 'Chờ ván'} · pot {tien(b.pot)}</span>
                  </button>
                );
              })}
            </div>
          </TabPanel>
          <TabPanel>
            <PokerTablesTab />
          </TabPanel>
          <TabPanel>
            <PokerBanTaoTab />
          </TabPanel>
          {chuSoHuu ? <TabPanel><PokerQuyBotTab /></TabPanel> : null}
          {chuSoHuu ? <TabPanel><PokerChoSanTab /></TabPanel> : null}
        </TabPanels>
      </Tabs>
    </div>
  );
}

function KetQua({ cmd, onClose }: { cmd: Command; onClose: () => void }) {
  const ok = cmd.status === 'SUCCEEDED';
  const code = cmd.lastError || (cmd.result && typeof cmd.result.loi === 'string' ? cmd.result.loi : '');
  return (
    <div>
      <InlineNotification kind={ok ? 'success' : 'warning'} lowContrast onClose={() => { onClose(); return false; }}
        title={ok ? 'Đã ghi lệnh' : 'Lệnh chưa xong'}
        subtitle={ok ? 'Áp vào ván tới.' : (HE_A_ERRORS[code] ?? (code || cmd.status))} />
      <p><Link className="cell-link" to={'/commands/' + cmd.id}>Xem lệnh</Link></p>
    </div>
  );
}

function moTa(l: Lenh) {
  const giu = l.mot_lan ? 'một ván' : 'giữ đến khi xóa';
  if (l.che_do === 'bai' && l.bai) {
    const tay = Object.entries(l.bai.la_tay).map(([g, cs]) => 'ghế ' + g + ' ' + cs.join(' ')).join(', ');
    const chung = l.bai.la_chung.length ? 'chung ' + l.bai.la_chung.join(' ') : '';
    return `Chỉ định bài (${giu}): ${[tay, chung].filter(Boolean).join('; ')}`;
  }
  return `${l.ket_qua === 'thua' ? 'Thua' : 'Thắng'} #${l.user_id} (${giu})`;
}

function BanForm({ ban, lenh, gui, dangGui }: { ban: Ban; lenh: Lenh[]; gui: (b: Record<string, unknown>) => void; dangGui: boolean }) {
  const [cheDo, setCheDo] = useState<'ket_qua' | 'bai'>('ket_qua');
  const [userId, setUserId] = useState<number | null>(() => chonNguoiTrongBan(ban, null));
  const [ketQua, setKetQua] = useState<'thang' | 'thua'>('thang');
  const [motLan, setMotLan] = useState(true);
  const [oTay, setOTay] = useState<Record<number, string[]>>({});
  const [oChung, setOChung] = useState<string[]>(['', '', '', '', '']);
  const [loiForm, setLoiForm] = useState('');
  const ngoi = gheNgoi(ban);
  const userIdHopLe = chonNguoiTrongBan(ban, userId);

  useEffect(() => {
    if (userId === null && userIdHopLe !== null) setUserId(userIdHopLe);
  }, [userId, userIdHopLe]);

  const guiKetQua = () => {
    if (!userIdHopLe) { setLoiForm('Chọn một người đang ngồi.'); return; }
    setLoiForm('');
    gui({ hanh_dong: 'tao', ban_id: ban.id, che_do: 'ket_qua', mot_lan: motLan, user_id: userIdHopLe, ket_qua: ketQua });
  };
  const guiBai = () => {
    const laTay: Record<string, string[]> = {};
    for (let g = 0; g < ban.soGhe; g++) {
      const cap = (oTay[g] || []).map((s) => s.trim()).filter(Boolean);
      if (!cap.length) continue;
      if (cap.length !== ban.soLaTay) { setLoiForm('Ghế ' + g + ' cần đủ ' + ban.soLaTay + ' lá, hoặc để trống cả ghế.'); return; }
      laTay[String(g)] = cap;
    }
    const laChung = oChung.map((s) => s.trim()).filter(Boolean);
    if (!Object.keys(laTay).length && !laChung.length) { setLoiForm('Điền lá của một ghế, hoặc các lá chung đầu tiên.'); return; }
    setLoiForm('');
    gui({ hanh_dong: 'tao', ban_id: ban.id, che_do: 'bai', mot_lan: motLan, la_tay: laTay, la_chung: laChung });
  };

  return (
    <div className="fr-poker" style={{ marginTop: '1rem' }}>
      <p><strong>{ban.ten} ({ban.id})</strong> · mù {tien(ban.muNho)}/{tien(ban.muLon)}{ban.van ? ` · ${VONG[ban.vong ?? 0] ?? 'Ván'} · pot ${tien(ban.pot)}` : ' · chưa chia'}</p>
      {ban.laChung.length ? <p className="fr-o-la">Bài chung {ban.laChung.map((c) => <La key={c + ban.van} ma={c} />)}</p> : null}
      {ngoi.map((g) => (
        <div key={g.ghe} className={'fr-ghe' + (userIdHopLe === g.userId ? ' is-chon' : '')}>
          <div>
            <button type="button" className="cell-link" onClick={() => { setUserId(g.userId); setCheDo('ket_qua'); }}>
              Ghế {g.ghe}: {g.ten}{g.laBot ? ' (bot)' : ''} #{g.userId}
            </button>
            {' '}<span className="num">{tien(g.stack)}</span>
            {g.bo ? <span className="muted"> đã úp</span> : null}
          </div>
          {g.laTay.length ? <p className="fr-o-la">{g.laTay.map((c, i) => <La key={g.ghe + '-' + i} ma={c} />)}</p> : null}
        </div>
      ))}
      {lenh.map((l) => (
        <div key={l.id}>
          <p>Lệnh đang chờ #{l.id}: {moTa(l)}{l.loi ? ` — ${l.loi}` : ''}</p>
          <Button kind="danger" size="sm" disabled={dangGui} onClick={() => gui({ hanh_dong: 'huy', id: l.id })}>Xóa lệnh</Button>
        </div>
      ))}
      <div className="fr-o-la">
        <Button kind={cheDo === 'ket_qua' ? 'primary' : 'ghost'} size="sm" onClick={() => setCheDo('ket_qua')}>Thắng / thua</Button>
        <Button kind={cheDo === 'bai' ? 'primary' : 'ghost'} size="sm" onClick={() => setCheDo('bai')}>Chỉ định lá</Button>
      </div>
      <label>
        <input type="checkbox" checked={motLan} onChange={(e) => setMotLan(e.target.checked)} /> Chỉ một ván
      </label>
      <p className="muted">Bỏ chọn thì lệnh giữ cho các ván sau, đến khi xóa.</p>
      {loiForm ? <InlineNotification kind="error" lowContrast hideCloseButton title="Chưa gửi" subtitle={loiForm} /> : null}
      {cheDo === 'ket_qua' ? (
        <div className="fr-o-la">
          <Button kind={ketQua === 'thang' ? 'primary' : 'ghost'} size="sm" onClick={() => setKetQua('thang')}>Thắng</Button>
          <Button kind={ketQua === 'thua' ? 'primary' : 'ghost'} size="sm" onClick={() => setKetQua('thua')}>Thua</Button>
          <Button kind="primary" size="sm" disabled={dangGui} onClick={guiKetQua}>Gửi lệnh ván tới</Button>
        </div>
      ) : (
        <div className="fr-poker">
          {Array.from({ length: ban.soGhe }, (_, g) => {
            const n = ban.ghe[g];
            const cap = (oTay[g] || []).concat(Array(ban.soLaTay).fill('')).slice(0, ban.soLaTay);
            return (
              <div key={g} className="fr-o-la">
                <span>Ghế {g}{n ? ` ${n.ten}` : ' trống'}</span>
                {cap.map((v, i) => (
                  <input key={i} className={'fr-la' + (/[hd]$/.test(v) ? ' fr-la--do' : '')} maxLength={2} value={v}
                    onChange={(e) => {
                      const tiep = cap.slice();
                      tiep[i] = chuanO(e.target.value).slice(0, 2);
                      setOTay({ ...oTay, [g]: tiep });
                    }} />
                ))}
              </div>
            );
          })}
          <div className="fr-o-la">
            <span>Bài chung</span>
            {oChung.map((v, i) => (
              <input key={i} className={'fr-la' + (/[hd]$/.test(v) ? ' fr-la--do' : '')} maxLength={2} value={v}
                onChange={(e) => {
                  const tiep = oChung.slice();
                  tiep[i] = chuanO(e.target.value).slice(0, 2);
                  setOChung(tiep);
                }} />
            ))}
          </div>
          <div><Button kind="primary" size="sm" disabled={dangGui} onClick={guiBai}>Gửi bài ván tới</Button></div>
        </div>
      )}
    </div>
  );
}
